import React, { useState, useEffect, useRef, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getDatabase, ref, onValue, get, update, runTransaction, onDisconnect,
  query as dbQuery, orderByChild, startAt, endAt,
} from 'firebase/database';
import { getAuth } from 'firebase/auth';
import {
  Box, Typography, Paper, Grid, Chip, Button, Divider, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, FormControl, InputLabel,
  Alert, Snackbar, CircularProgress, InputAdornment,
} from '@mui/material';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { MAIN_COLOR, FONT_FAMILY } from '../common/sharedFunctions';
import { registerCCTranslations } from './aiCallCenterI18n';

// Statuses shown in the live real-time queue
const ACTIVE_STATUSES = ['PENDING', 'CLAIMED', 'PROPOSED', 'MISSED', 'CANCELLED_BY_CUSTOMER', 'ACCEPTED', 'REJECTED', 'CANCELLED_BY_AGENT', 'PROPOSAL_EXPIRED'];

const TERMINAL_AUDIO_STATUSES = new Set([
  'BOOKING_COMPLETE', 'BOOKING_CANCELLED',
  'REJECTED', 'AGENT_REJECTED',
  'EXPIRED', 'MISSED',
  'PROPOSAL_EXPIRED',
  'CANCELLED_BY_CUSTOMER', 'CANCELLED_ACKNOWLEDGED',
]);

// CHAT-REQUEST statuses (the call-center proposal lifecycle: PENDING -> CLAIMED -> PROPOSED -> ACCEPTED/REJECTED).
// IMPORTANT: here "ACCEPTED" means the CUSTOMER accepted the agent's proposal (hit YES). At that moment the
// /bookings record is created as NEW (waiting for a driver). This is NOT booking.status 'ACCEPTED'
// (= a driver accepted the ride; see BOOKING_STATUS_META below). The two are different statuses on
// different entities (chatRequest vs booking) and must not be conflated.
const STATUS_META = {
  PENDING:                { key: 'cc_st_pending',            label: 'Waiting',           color: '#92400E', bg: '#FEF3C7', border: '#F59E0B' },
  CLAIMED:                { key: 'cc_st_claimed',            label: 'In Progress',        color: '#1E40AF', bg: '#EFF6FF', border: '#3B82F6' },
  PROPOSED:               { key: 'cc_st_proposed',           label: 'Awaiting Customer',  color: '#9A3412', bg: '#FFF7ED', border: '#F97316' },
  ACCEPTED:               { key: 'cc_st_accepted',           label: 'Accepted',           color: '#065F46', bg: '#ECFDF5', border: '#10B981' },
  REJECTED:               { key: 'cc_st_rejected',           label: 'Declined by Customer', color: '#991B1B', bg: '#FEF2F2', border: '#EF4444' },
  AGENT_REJECTED:         { key: 'cc_st_agent_rejected',     label: 'Rejected by Agent',  color: '#991B1B', bg: '#FEF2F2', border: '#EF4444' },
  EXPIRED:                { key: 'cc_st_expired',            label: 'Expired',            color: '#374151', bg: '#F3F4F6', border: '#9CA3AF' },
  MISSED:                 { key: 'cc_st_missed',             label: 'Missed',             color: '#991B1B', bg: '#FEF2F2', border: '#EF4444' },
  CANCELLED_BY_CUSTOMER:  { key: 'cc_st_cancelled_customer', label: 'Cancelled by Customer', color: '#374151', bg: '#F3F4F6', border: '#9CA3AF' },
  CANCELLED_BY_AGENT:     { key: 'cc_st_cancelled_agent',    label: 'Cancelled by Agent',    color: '#7C3AED', bg: '#F5F3FF', border: '#8B5CF6' },
  CANCELLED_ACKNOWLEDGED: { key: 'cc_st_cancelled_ack',      label: 'Acknowledged',           color: '#374151', bg: '#F3F4F6', border: '#9CA3AF' },
  PROPOSAL_EXPIRED:       { key: 'cc_st_proposal_expired',   label: 'Proposal Expired',       color: '#374151', bg: '#F3F4F6', border: '#9CA3AF' },
  BOOKING_CANCELLED:      { key: 'cc_st_booking_cancelled',  label: 'Booking Cancelled',      color: '#991B1B', bg: '#FEE2E2', border: '#EF4444' },
  BOOKING_COMPLETE:       { key: 'cc_st_booking_complete',   label: 'Booking Complete',       color: '#065F46', bg: '#ECFDF5', border: '#10B981' },
};

// /bookings ENGINE statuses of the confirmed booking (watched live via confirmedBookingId once the
// customer accepted the proposal). booking.status 'ACCEPTED' here = a DRIVER accepted the ride —
// distinct from the chatRequest 'ACCEPTED' above (customer accepted the proposal, booking still NEW).
const BOOKING_STATUS_META = {
  NEW:       { key: 'cc_bk_new',       label: 'Waiting for driver',   color: '#1E40AF', bg: '#EFF6FF', border: '#3B82F6', icon: '🔍' },
  ACCEPTED:  { key: 'cc_bk_accepted',  label: 'Driver accepted',      color: '#065F46', bg: '#ECFDF5', border: '#10B981', icon: '🚕' },
  ARRIVED:   { key: 'cc_bk_arrived',   label: 'Driver arrived',       color: '#92400E', bg: '#FEF3C7', border: '#F59E0B', icon: '📍' },
  STARTED:   { key: 'cc_bk_started',   label: 'Trip in progress',     color: '#4C1D95', bg: '#F5F3FF', border: '#8B5CF6', icon: '🚗' },
  REACHED:   { key: 'cc_bk_reached',   label: 'Reached destination',  color: '#92400E', bg: '#FFF7ED', border: '#F97316', icon: '🏁' },
  PAID:      { key: 'cc_bk_paid',      label: 'Payment complete',     color: '#065F46', bg: '#ECFDF5', border: '#10B981', icon: '💚' },
  COMPLETE:  { key: 'cc_bk_complete',  label: 'Completed',            color: '#065F46', bg: '#ECFDF5', border: '#10B981', icon: '✅' },
  CANCELLED: { key: 'cc_bk_cancelled', label: 'Cancelled',            color: '#991B1B', bg: '#FEF2F2', border: '#EF4444', icon: '❌' },
};

const CANCEL_REASONS = [
  { value: 'customer_issue',       key: 'cc_cr_customer_issue', label: 'Customer issue' },
  { value: 'duplicate',            key: 'cc_cr_duplicate',      label: 'Duplicate request' },
  { value: 'out_of_service_area',  key: 'cc_cr_out_of_area',    label: 'Out of service area' },
  { value: 'unable_to_fulfill',    key: 'cc_cr_unable',         label: 'Unable to fulfill' },
  { value: 'test_fake',            key: 'cc_cr_test',           label: 'Test / fake request' },
  { value: 'other',                key: 'cc_cr_other',          label: 'Other' },
];

const useStyles = makeStyles({
  root:             { display: 'flex', height: 'calc(100vh - 112px)', fontFamily: FONT_FAMILY },
  header:           { padding: '12px 24px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title:            { fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: 22, color: MAIN_COLOR },
  queuePanel:       { width: 320, borderRight: '1px solid #eee', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  queueList:        { flex: 1, overflowY: 'auto' },
  detailPanel:      { flex: 1, overflowY: 'auto', padding: 24 },
  queueItem:        { padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', '&:hover': { backgroundColor: '#f9f9f9' } },
  queueItemSelected:{ backgroundColor: '#e8f0fe' },
  elapsed:          { fontFamily: 'monospace', fontSize: 13, color: '#888' },
  detailLabel:      { fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: 13, color: '#555', marginBottom: 2 },
  detailValue:      { fontFamily: FONT_FAMILY, fontSize: 14, marginBottom: 12 },
  actionRow:        { display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' },
  cancelledBanner:  { backgroundColor: '#ffebee', border: '2px solid #f44336', borderRadius: 8, padding: 12, marginBottom: 16 },
  proposedBanner:   { backgroundColor: '#FFF7ED', border: '2px solid #F97316', borderRadius: 8, padding: 12, marginBottom: 16 },
  emptyQueue:       { padding: 32, textAlign: 'center', color: '#999', fontFamily: FONT_FAMILY },
  historyControls:  { padding: '10px 12px', borderBottom: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: 8 },
  proposedCard:     { backgroundColor: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 8, padding: 12, marginTop: 12 },
});

function StatusChip({ status }) {
  const { t } = useTranslation('aicallcenter');
  const meta = STATUS_META[status] || { label: status, color: '#374151', bg: '#F3F4F6', border: '#9CA3AF' };
  return (
    <Chip
      label={meta.key ? t(meta.key) : meta.label}
      size="small"
      sx={{
        backgroundColor: meta.bg,
        color: meta.color,
        border: `1px solid ${meta.border}`,
        fontFamily: FONT_FAMILY,
        fontWeight: 600,
        fontSize: 11,
      }}
    />
  );
}

function ElapsedTimer({ createdAt }) {
  const [elapsed, setElapsed] = useState('');
  useEffect(() => {
    const tick = () => {
      const secs = Math.floor((Date.now() - createdAt) / 1000);
      const m = Math.floor(secs / 60).toString().padStart(2, '0');
      const s = (secs % 60).toString().padStart(2, '0');
      setElapsed(`${m}:${s}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [createdAt]);
  return <span>{elapsed}</span>;
}

function formatTs(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function AiCallCenter() {
  const classes = useStyles();
  registerCCTranslations(); // must run inside component, after i18n.init()
  const { t } = useTranslation('aicallcenter');
  const navigate = useNavigate();
  const auth = useSelector(state => state.auth);
  const db = getDatabase();
  const firebaseAuth = getAuth();

  const [activeTab, setActiveTab]           = useState(0); // 0=active 1=accepted 2=history
  const [requests, setRequests]             = useState([]);
  const [selectedId, setSelectedId]         = useState(null);
  const [toast, setToast]                   = useState({ open: false, msg: '', severity: 'info' });
  const [cancelDialog, setCancelDialog]     = useState(false);
  const [cancelReason, setCancelReason]     = useState('');
  const [cancelNote, setCancelNote]         = useState('');
  const [submitting, setSubmitting]         = useState(false);
  const [ackDialog, setAckDialog]           = useState(false);
  const [ackNote, setAckNote]               = useState('');
  const [audioLoading, setAudioLoading]     = useState(false);
  const [audioUrl, setAudioUrl]             = useState(null);

  // History tab state
  const today = new Date().toISOString().slice(0, 10);
  const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
  const [historyRecords, setHistoryRecords]         = useState([]);
  const [historyLoading, setHistoryLoading]         = useState(false);
  const [historyDateStart, setHistoryDateStart]     = useState(sevenDaysAgo);
  const [historyDateEnd, setHistoryDateEnd]         = useState(today);
  const [searchText, setSearchText]                 = useState('');

  const [liveBooking, setLiveBooking]           = useState(null);

  const heartbeatRef          = useRef(null);
  const audioRef              = useRef(null);
  const prevCountRef          = useRef(0);
  const liveBookingUnsubRef   = useRef(null);

  const agentUid  = auth?.profile?.uid;
  const agentName = auth?.profile ? `${auth.profile.firstName || ''} ${auth.profile.lastName || ''}`.trim() : '';
  const isAdmin   = auth?.profile?.usertype === 'admin';

  // Tab 1 ("Accepted") lists chatRequests the CUSTOMER accepted (proposal YES). The booking is still
  // NEW at this point (waiting for a driver) — `r.status` here is the chatRequest status, not booking status.
  const allItems  = activeTab === 2 ? historyRecords.filter(r => r.status !== 'ACCEPTED')
    : activeTab === 1 ? requests.filter(r => r.status === 'ACCEPTED')
    : requests.filter(r => r.status !== 'ACCEPTED' && r.status !== 'REJECTED');
  const filtered  = allItems.filter(r => {
    if (!searchText.trim()) return true;
    const t = searchText.toLowerCase();
    return (r.customerName || '').toLowerCase().includes(t) || (r.customerPhone || '').includes(t);
  });
  const selected = [...requests, ...historyRecords].find(r => r.id === selectedId) || null;

  useEffect(() => { setAudioUrl(null); setAudioLoading(false); }, [selectedId]);

  // Live booking status watcher — fires when an ACCEPTED chatRequest has a confirmedBookingId
  useEffect(() => {
    if (liveBookingUnsubRef.current) {
      liveBookingUnsubRef.current();
      liveBookingUnsubRef.current = null;
    }
    setLiveBooking(null);
    const liveBookingId = selected?.confirmedBookingId || selected?.proposedBookingId;
    if (selected?.status === 'ACCEPTED' && liveBookingId) {
      liveBookingUnsubRef.current = onValue(
        ref(db, `bookings/${liveBookingId}`),
        snap => setLiveBooking(snap.exists() ? snap.val() : null)
      );
    }
    return () => {
      if (liveBookingUnsubRef.current) {
        liveBookingUnsubRef.current();
        liveBookingUnsubRef.current = null;
      }
    };
  }, [db, selectedId, selected?.status, selected?.confirmedBookingId, selected?.proposedBookingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time queue listener (active statuses only)
  useEffect(() => {
    const qRef = ref(db, 'chatRequests');
    const handler = onValue(qRef, snap => {
      if (!snap.exists()) { setRequests([]); return; }
      const arr = [];
      snap.forEach(child => {
        const val = child.val();
        if (typeof val !== 'object' || val === null) return;
        if (ACTIVE_STATUSES.includes(val.status)) arr.push({ id: child.key, ...val });
      });
      arr.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      const pendingCount = arr.filter(r => r.status === 'PENDING').length;
      if (pendingCount > prevCountRef.current && audioRef.current) audioRef.current.play().catch(() => {});
      prevCountRef.current = pendingCount;
      setRequests(arr);
    });
    return () => handler();
  }, [db]);

  // Load history when tab switches to history
  useEffect(() => {
    if (activeTab === 2) loadHistory();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const startTs = new Date(historyDateStart + 'T00:00:00').getTime();
      const endTs   = new Date(historyDateEnd   + 'T23:59:59').getTime();
      const q = dbQuery(ref(db, 'chatRequests'), orderByChild('createdAt'), startAt(startTs), endAt(endTs));
      const snap = await get(q);
      const arr = [];
      if (snap.exists()) snap.forEach(child => {
        const val = child.val();
        if (typeof val === 'object' && val !== null) arr.push({ id: child.key, ...val });
      });
      arr.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setHistoryRecords(arr);
    } catch (e) {
      setToast({ open: true, msg: t('cc_toast_history_error') + (e.message || 'error'), severity: 'error' });
    } finally {
      setHistoryLoading(false);
    }
  }, [db, historyDateStart, historyDateEnd]);

  // Auto-restore agent's active claim after page refresh
  useEffect(() => {
    if (!agentUid || selectedId || requests.length === 0) return;
    const mine = requests.find(r => r.status === 'CLAIMED' && r.claimedBy === agentUid);
    if (mine) setSelectedId(mine.id);
  }, [requests, agentUid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Agent heartbeat
  useEffect(() => {
    if (!agentUid) return;
    const sessionRef = ref(db, `agentSessions/${agentUid}`);
    const writeHeartbeat = () => update(sessionRef, { lastSeenAt: Date.now(), pageFocused: document.hasFocus(), selectedRequestId: selectedId || null });
    writeHeartbeat();
    heartbeatRef.current = setInterval(writeHeartbeat, 30000);
    return () => {
      clearInterval(heartbeatRef.current);
      update(sessionRef, { lastSeenAt: Date.now(), pageFocused: false, selectedRequestId: null });
    };
  }, [db, agentUid, selectedId]);

  // Browser-close detection
  useEffect(() => {
    if (!selectedId || !agentUid) return;
    const reqRef = ref(db, `chatRequests/${selectedId}/lastAgentHeartbeatAt`);
    const disconnect = onDisconnect(reqRef);
    disconnect.set(0).catch(() => {});
    return () => disconnect.cancel().catch(() => {});
  }, [db, agentUid, selectedId]);


  const handleClaim = async (reqId) => {
    try {
      const result = await runTransaction(ref(db, `chatRequests/${reqId}`), current => {
        if (!current) return current;
        if (current.status !== 'PENDING' && current.status !== 'MISSED') return undefined;
        if (current.status === 'CLAIMED' && current.claimedBy !== agentUid) return undefined;
        return { ...current, status: 'CLAIMED', claimedAt: Date.now(), claimedBy: agentUid, claimedByName: agentName, lastAgentHeartbeatAt: Date.now() };
      });
      if (!result.committed) {
        setToast({ open: true, msg: t('cc_toast_already_claimed'), severity: 'warning' });
        return;
      }
      setSelectedId(reqId);
    } catch {
      setToast({ open: true, msg: t('cc_toast_claim_failed'), severity: 'error' });
    }
  };

  const handleCancelSubmit = async () => {
    if (!cancelReason || !selectedId) return;
    setSubmitting(true);
    try {
      const updates = {};
      updates[`chatRequests/${selectedId}/status`]      = 'AGENT_REJECTED';
      updates[`chatRequests/${selectedId}/closeReason`] = 'agent_cancelled';
      updates[`chatRequests/${selectedId}/cancelReason`]= cancelReason;
      updates[`chatRequests/${selectedId}/agentNote`]   = cancelNote;
      updates[`chatRequests/${selectedId}/closedAt`]    = Date.now();
      updates[`chatRequests/${selectedId}/handledBy`]   = agentUid;
      await update(ref(db), updates);
      setToast({ open: true, msg: t('cc_toast_request_cancelled'), severity: 'info' });
      setCancelDialog(false); setCancelReason(''); setCancelNote('');
      setSelectedId(null);
    } finally { setSubmitting(false); }
  };

  const handleOpenAddBooking = async () => {
    if (!selected || !agentUid) return;
    await update(ref(db, `callCenterContext/${agentUid}`), {
      agentUid, chatRequestId: selected.id,
      customerUid: selected.customerUid, customerName: selected.customerName || '',
      customerPhone: selected.customerPhone || '',
      createdAt: Date.now(), expiresAt: Date.now() + 10 * 60 * 1000,
    });
    navigate('/addbookings?fromCallCenter=true');
  };

  const handleAckCancellation = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    try {
      await update(ref(db, `chatRequests/${selectedId}`), {
        status: 'CANCELLED_ACKNOWLEDGED', acknowledgedAt: Date.now(),
        acknowledgedBy: agentUid, agentNote: ackNote,
      });
      setAckDialog(false); setAckNote('');
      setSelectedId(null);
    } finally { setSubmitting(false); }
  };

  const handlePlayAudio = async (storagePath) => {
    setAudioLoading(true); setAudioUrl(null);
    try {
      const token = await firebaseAuth.currentUser.getIdToken();
      const response = await fetch(
        'https://europe-west1-sawiride.cloudfunctions.net/getChatRequestAudioAccess',
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ storagePath }) }
      );
      if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.error || `HTTP ${response.status}`); }
      const data = await response.json();
      setAudioUrl(data.url);
    } catch (e) {
      setToast({ open: true, msg: t('cc_toast_audio_error') + (e.message || 'error'), severity: 'error' });
    } finally { setAudioLoading(false); }
  };

  const canCancel = selected && (selected.claimedBy === agentUid || isAdmin);

  return (
    <Box>
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" gap={1}>
          <HeadsetMicIcon sx={{ color: MAIN_COLOR }} />
          <Typography className={classes.title}>{t('cc_title')}</Typography>
        </Box>
        <Typography variant="body2" color="textSecondary">
          {requests.filter(r => r.status === 'PENDING').length} {t('cc_pending')} &nbsp;·&nbsp;
          {requests.filter(r => r.status === 'CLAIMED').length} {t('cc_in_progress')} &nbsp;·&nbsp;
          {requests.filter(r => r.status === 'PROPOSED').length} {t('cc_awaiting_customer')}
        </Typography>
      </Box>

      <audio ref={audioRef} src="/sounds/new-request.mp3" preload="none" />

      <Box className={classes.root}>
        {/* Queue / History panel */}
        <Box className={classes.queuePanel}>
          <Tabs value={activeTab} onChange={(_, v) => { setActiveTab(v); setSelectedId(null); }} sx={{ borderBottom: '1px solid #eee', minHeight: 40 }}>
            <Tab label={t('cc_active')} sx={{ fontFamily: FONT_FAMILY, fontSize: 13, minHeight: 40, textTransform: 'none' }} />
            <Tab label={t('cc_accepted_tab')} sx={{ fontFamily: FONT_FAMILY, fontSize: 13, minHeight: 40, textTransform: 'none' }} />
            <Tab label={t('cc_history')} sx={{ fontFamily: FONT_FAMILY, fontSize: 13, minHeight: 40, textTransform: 'none' }} />
          </Tabs>

          {/* Search bar (both tabs) */}
          <Box sx={{ p: 1, borderBottom: '1px solid #f0f0f0' }}>
            <TextField
              size="small" fullWidth placeholder={t('cc_search_ph')}
              value={searchText} onChange={e => setSearchText(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#aaa' }} /></InputAdornment> }}
              sx={{ '& .MuiInputBase-root': { fontFamily: FONT_FAMILY, fontSize: 13 } }}
            />
          </Box>

          {/* History date filter */}
          {activeTab === 2 && (
            <Box className={classes.historyControls}>
              <Box display="flex" gap={1} alignItems="center">
                <TextField size="small" type="date" label={t('cc_from')} value={historyDateStart}
                  onChange={e => setHistoryDateStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, '& .MuiInputBase-root': { fontFamily: FONT_FAMILY, fontSize: 12 } }} />
                <TextField size="small" type="date" label={t('cc_to')} value={historyDateEnd}
                  onChange={e => setHistoryDateEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, '& .MuiInputBase-root': { fontFamily: FONT_FAMILY, fontSize: 12 } }} />
              </Box>
              <Button size="small" variant="contained" onClick={loadHistory} disabled={historyLoading}
                sx={{ fontFamily: FONT_FAMILY, textTransform: 'none', fontSize: 12, backgroundColor: MAIN_COLOR }}>
                {historyLoading ? <CircularProgress size={14} color="inherit" /> : t('cc_search')}
              </Button>
            </Box>
          )}

          <Box className={classes.queueList}>
            {activeTab === 2 && historyLoading && (
              <Box textAlign="center" pt={4}><CircularProgress size={24} /></Box>
            )}
            {filtered.length === 0 && !historyLoading ? (
              <Box className={classes.emptyQueue}>
                {activeTab === 0 ? t('cc_no_active') : activeTab === 1 ? t('cc_no_accepted') : t('cc_no_records')}
              </Box>
            ) : filtered.map(req => (
              <Box
                key={req.id}
                className={`${classes.queueItem} ${req.id === selectedId ? classes.queueItemSelected : ''}`}
                onClick={() => setSelectedId(req.id)}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={0.5}>
                  <Box>
                    <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: 13 }}>
                      {req.customerName || t('cc_unknown')}
                    </Typography>
                    <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: '#666' }}>
                      {req.customerPhone || ''}
                    </Typography>
                  </Box>
                  <StatusChip status={req.status} />
                </Box>
                <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                  <AccessTimeIcon sx={{ fontSize: 12, color: '#bbb' }} />
                  <Typography className={classes.elapsed} style={{ fontSize: 11 }}>
                    {activeTab === 0 && req.createdAt ? <ElapsedTimer createdAt={req.createdAt} /> : formatTs(req.createdAt)}
                  </Typography>
                </Box>
                {req.aiContext?.extractedDropPhrase && (
                  <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: '#888', marginTop: 2 }}>
                    → {req.aiContext.extractedDropPhrase}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Detail panel */}
        <Box className={classes.detailPanel}>
          {!selected ? (
            <Box textAlign="center" mt={8} color="#ccc">
              <HeadsetMicIcon sx={{ fontSize: 64 }} />
              <Typography style={{ fontFamily: FONT_FAMILY, marginTop: 8 }}>{t('cc_select_request')}</Typography>
            </Box>
          ) : (
            <Box>
              {/* Status banners */}
              {selected.status === 'CANCELLED_BY_CUSTOMER' && (
                <Box className={classes.cancelledBanner}>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#c62828' }}>
                    {t('cc_banner_cancelled')}
                  </Typography>
                </Box>
              )}
              {selected.status === 'PROPOSED' && (
                <Box className={classes.proposedBanner}>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#9A3412' }}>
                    {t('cc_banner_proposed')}
                  </Typography>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: '#555', marginTop: 4 }}>
                    {t('cc_proposed_at')} {formatTs(selected.proposedAt)}
                  </Typography>
                </Box>
              )}
              {selected.status === 'ACCEPTED' && (() => {
                const bm = liveBooking ? (BOOKING_STATUS_META[liveBooking.status] || { label: liveBooking.status, color: '#374151', bg: '#F3F4F6', border: '#9CA3AF', icon: '📋' }) : null;
                return (
                  <Box sx={{ backgroundColor: '#ECFDF5', border: '2px solid #10B981', borderRadius: 2, p: 1.5, mb: 2 }}>
                    <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#065F46', marginBottom: 6 }}>
                      {t('cc_banner_accepted')}
                    </Typography>
                    {liveBooking ? (
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13, color: '#374151' }}>{t('cc_booking_status')}</Typography>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: 1, backgroundColor: bm.bg, border: `1px solid ${bm.border}` }}>
                            <span>{bm.icon}</span>
                            <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, fontWeight: 700, color: bm.color }}>{bm.key ? t(bm.key) : bm.label}</Typography>
                          </Box>
                        </Box>
                        {liveBooking.pickup?.add && (
                          <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: '#555' }}>📍 {t('cc_from_label')} {liveBooking.pickup.add}</Typography>
                        )}
                        {liveBooking.drop?.add && (
                          <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: '#555' }}>🏁 {t('cc_to_label')} {liveBooking.drop.add}</Typography>
                        )}
                        {liveBooking.driver_name && (
                          <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: '#555', marginTop: 2 }}>🚕 {t('cc_driver')} {liveBooking.driver_name}</Typography>
                        )}
                        {liveBooking.estimate != null && (
                          <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: '#555' }}>💰 {t('cc_fare')} {liveBooking.estimate} MRU · {liveBooking.payment_mode}</Typography>
                        )}
                        {liveBooking.reference && (
                          <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 11, color: '#888', marginTop: 2 }}>{t('cc_ref')} {liveBooking.reference}</Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: '#888' }}>{t('cc_loading_booking')}</Typography>
                    )}
                  </Box>
                );
              })()}
              {selected.status === 'REJECTED' && (
                <Box sx={{ backgroundColor: '#FEF2F2', border: '2px solid #EF4444', borderRadius: 2, p: 1.5, mb: 2 }}>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#991B1B' }}>
                    {t('cc_banner_declined')}
                    {selected.closeReason ? ` — ${selected.closeReason}` : ''}
                  </Typography>
                </Box>
              )}
              {selected.status === 'BOOKING_CANCELLED' && (
                <Box sx={{ backgroundColor: '#FEE2E2', border: '1px solid #EF4444', borderRadius: 2, p: 1.5, mb: 2 }}>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#991B1B' }}>
                    {t('cc_banner_booking_cancelled')} ({selected.closeReason || t('cc_unknown_reason')})
                  </Typography>
                </Box>
              )}
              {selected.status === 'AGENT_REJECTED' && (
                <Box sx={{ backgroundColor: '#FEF2F2', border: '2px solid #EF4444', borderRadius: 2, p: 1.5, mb: 2 }}>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#991B1B' }}>
                    {t('cc_banner_agent_rejected')}{selected.agentNote ? `: "${selected.agentNote}"` : ''}
                  </Typography>
                </Box>
              )}
              {selected.status === 'CANCELLED_BY_AGENT' && (
                <Box sx={{ backgroundColor: '#F5F3FF', border: '2px solid #8B5CF6', borderRadius: 2, p: 1.5, mb: 2 }}>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#7C3AED' }}>
                    {t('cc_banner_prebooking_cancelled')}
                  </Typography>
                  {selected.cancelledByName && (
                    <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: '#555', marginTop: 4 }}>
                      {t('cc_cancelled_by')} {selected.cancelledByName}
                    </Typography>
                  )}
                </Box>
              )}
              {selected.status === 'EXPIRED' && (
                <Box sx={{ backgroundColor: '#F3F4F6', border: '1px solid #9CA3AF', borderRadius: 2, p: 1.5, mb: 2 }}>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#374151' }}>
                    {t('cc_banner_expired')}
                  </Typography>
                </Box>
              )}
              {selected.status === 'PROPOSAL_EXPIRED' && (
                <Box sx={{ backgroundColor: '#F3F4F6', border: '1px solid #9CA3AF', borderRadius: 2, p: 1.5, mb: 2 }}>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#374151' }}>
                    {t('cc_banner_proposal_expired')}
                  </Typography>
                </Box>
              )}

              {/* Core info */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography className={classes.detailLabel}>{t('cc_customer')}</Typography>
                  <Typography className={classes.detailValue}>{selected.customerName || '—'}</Typography>

                  <Typography className={classes.detailLabel}>{t('cc_phone')}</Typography>
                  <Typography className={classes.detailValue}>{selected.customerPhone || '—'}</Typography>

                  <Typography className={classes.detailLabel}>{t('cc_status')}</Typography>
                  <Box mb={1.5}><StatusChip status={selected.status} /></Box>

                  <Typography className={classes.detailLabel}>{t('cc_created')}</Typography>
                  <Typography className={classes.detailValue}>{formatTs(selected.createdAt)}</Typography>

                  {selected.claimedByName && (
                    <>
                      <Typography className={classes.detailLabel}>{t('cc_handled_by')}</Typography>
                      <Typography className={classes.detailValue}>{selected.claimedByName}</Typography>
                    </>
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  {selected.customerLocation && (
                    <>
                      <Typography className={classes.detailLabel}>{t('cc_gps')}</Typography>
                      <Typography className={classes.detailValue}>
                        {selected.customerLocation.lat?.toFixed(5)}, {selected.customerLocation.lng?.toFixed(5)}
                      </Typography>
                    </>
                  )}

                  {selected.aiContext && (selected.aiContext.extractedPickupPhrase || selected.aiContext.extractedDropPhrase) && (
                    <>
                      <Typography className={classes.detailLabel}>{t('cc_ai_understood')}</Typography>
                      <Typography className={classes.detailValue} style={{ whiteSpace: 'pre-line' }}>
                        {selected.aiContext.extractedPickupPhrase ? `${t('cc_ai_from')}: ${selected.aiContext.extractedPickupPhrase}` : ''}
                        {selected.aiContext.extractedDropPhrase  ? `\n${t('cc_ai_to')}: ${selected.aiContext.extractedDropPhrase}`   : ''}
                        {selected.aiContext.failureReason        ? `\n${t('cc_ai_reason')}: ${selected.aiContext.failureReason}`     : ''}
                      </Typography>
                    </>
                  )}

                  {selected.payload?.detectedLang && (
                    <>
                      <Typography className={classes.detailLabel}>{t('cc_language')}</Typography>
                      <Typography className={classes.detailValue}>{selected.payload.detectedLang.toUpperCase()}</Typography>
                    </>
                  )}
                </Grid>
              </Grid>

              {/* Location not identified warning */}
              {selected.aiContext?.escalationReason === 'location_not_identified' && (
                <Box mt={2} mb={1} sx={{ p: 1.5, backgroundColor: '#FFF3E0', border: '2px solid #FF9800', borderRadius: 2 }}>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 'bold', color: '#E65100', fontSize: 13 }}>
                    {t('cc_could_not_identify')} "{selected.aiContext.unresolvedPlace}"
                  </Typography>
                  {selected.aiContext.partialPickup && (
                    <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: '#555', marginTop: 4 }}>
                      {t('cc_confirmed_pickup')} {selected.aiContext.partialPickup}
                    </Typography>
                  )}
                  {selected.aiContext.partialDrop && (
                    <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: '#555', marginTop: 2 }}>
                      {t('cc_confirmed_drop')} {selected.aiContext.partialDrop}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Proposed booking details */}
              {selected.proposedPayload && (
                <Box className={classes.proposedCard}>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: 13, color: '#065F46', marginBottom: 6 }}>
                    {t('cc_proposed_booking')}
                  </Typography>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13 }}>
                    📍 {t('cc_from_label')} {selected.proposedPayload.pickup?.add || selected.proposedPayload.pickup?.description || '—'}
                  </Typography>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13 }}>
                    🏁 {t('cc_to_label')} {selected.proposedPayload.drop?.add || selected.proposedPayload.drop?.description || '—'}
                  </Typography>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13 }}>
                    🚗 {t('cc_type')} {selected.proposedPayload.carDetails?.name || selected.proposedPayload.carType || '—'}
                  </Typography>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13 }}>
                    💰 {t('cc_fare')} {(typeof selected.proposedPayload.estimate === 'object' ? selected.proposedPayload.estimate?.estimateFare : selected.proposedPayload.estimate) ?? '—'} MRU &nbsp;·&nbsp; {selected.proposedPayload.payment_mode}
                  </Typography>
                  {selected.proposedBookingId && (
                    <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 11, color: '#888', marginTop: 4 }}>
                      {t('cc_booking_id')} {selected.proposedBookingId}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Audio */}
              {(selected.payload && (selected.payload.audioStoragePath || selected.payload.hasAudio)) && (
                <Box mt={2} p={1.5} sx={{ border: '1px solid #ddd', borderRadius: 2, backgroundColor: '#fafafa' }}>
                  <Typography className={classes.detailLabel} style={{ fontWeight: 700, marginBottom: 6 }}>{t('cc_customer_audio')}</Typography>
                  {selected.payload.audioStoragePath ? (
                    audioUrl ? (
                      <audio controls src={audioUrl} style={{ width: '100%', marginTop: 4 }} />
                    ) : (
                      <Button size="small" variant="contained" startIcon={<PlayArrowIcon />} disabled={audioLoading}
                        onClick={() => handlePlayAudio(selected.payload.audioStoragePath)}
                        style={{ fontFamily: FONT_FAMILY, textTransform: 'none', marginTop: 4, backgroundColor: '#1976d2' }}>
                        {audioLoading ? t('cc_loading_audio') : t('cc_play_audio')}
                      </Button>
                    )
                  ) : (
                    <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: TERMINAL_AUDIO_STATUSES.has(selected.status) ? '#6B7280' : '#e65100', marginTop: 4 }}>
                      {TERMINAL_AUDIO_STATUSES.has(selected.status)
                        ? t('cc_audio_deleted')
                        : t('cc_audio_failed')}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Text messages */}
              {selected.payload?.textMessages?.length > 0 && (
                <Box mt={2}>
                  <Typography className={classes.detailLabel}>{t('cc_customer_messages')}</Typography>
                  {selected.payload.textMessages.map((m, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 1, mb: 0.5 }}>
                      <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13 }}>{m.text}</Typography>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Transcription */}
              {selected.payload?.transcription && (
                <Box mt={1}>
                  <Typography className={classes.detailLabel}>{t('cc_transcription')}</Typography>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13, fontStyle: 'italic', color: '#444' }}>
                    {selected.payload.transcription}
                  </Typography>
                </Box>
              )}

              {/* Agent note (for history) */}
              {selected.agentNote && (
                <Box mt={1}>
                  <Typography className={classes.detailLabel}>{t('cc_agent_note')}</Typography>
                  <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13, color: '#444' }}>{selected.agentNote}</Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Action buttons */}
              <Box className={classes.actionRow}>
                {(selected.status === 'PENDING' || selected.status === 'MISSED') && (
                  <Button variant="contained" onClick={() => handleClaim(selected.id)}
                    style={{ backgroundColor: MAIN_COLOR, textTransform: 'none', fontFamily: FONT_FAMILY }}>
                    {t('cc_claim')}
                  </Button>
                )}

                {selected.status === 'CLAIMED' && selected.claimedBy === agentUid && (
                  <Button variant="contained" onClick={handleOpenAddBooking}
                    style={{ backgroundColor: '#1976d2', textTransform: 'none', fontFamily: FONT_FAMILY }}>
                    {t('cc_add_booking')}
                  </Button>
                )}

                {selected.status === 'PROPOSED' && (
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13, color: '#9A3412', fontStyle: 'italic' }}>
                      {t('cc_waiting_confirm')}
                    </Typography>
                    <Button variant="outlined" color="error" size="small" disabled={submitting}
                      style={{ textTransform: 'none', fontFamily: FONT_FAMILY }}
                      onClick={async () => {
                        setSubmitting(true);
                        try {
                          const updates = {};
                          updates[`chatRequests/${selected.id}/status`]      = 'AGENT_REJECTED';
                          updates[`chatRequests/${selected.id}/closeReason`] = 'proposal_cancelled_by_agent';
                          updates[`chatRequests/${selected.id}/closedAt`]    = Date.now();
                          await update(ref(db), updates);
                        } finally { setSubmitting(false); }
                      }}>
                      {t('cc_cancel_proposal')}
                    </Button>
                  </Box>
                )}

                {canCancel && !['CANCELLED_BY_CUSTOMER', 'PROPOSED', 'ACCEPTED', 'REJECTED', 'AGENT_REJECTED', 'EXPIRED', 'CANCELLED_ACKNOWLEDGED'].includes(selected.status) && (
                  <Button variant="outlined" color="error" onClick={() => setCancelDialog(true)}
                    style={{ textTransform: 'none', fontFamily: FONT_FAMILY }}>
                    {t('cc_reject_request')}
                  </Button>
                )}

                {selected.status === 'CANCELLED_BY_CUSTOMER' && (
                  <Button variant="contained" color="error" onClick={() => setAckDialog(true)}
                    style={{ textTransform: 'none', fontFamily: FONT_FAMILY }}>
                    {t('cc_ack_cancellation')}
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Cancel / Reject dialog */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontFamily: FONT_FAMILY }}>{t('cc_reject_request_title')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>{t('cc_reason')}</InputLabel>
            <Select value={cancelReason} label={t('cc_reason')} onChange={e => setCancelReason(e.target.value)}>
              {CANCEL_REASONS.map(r => <MenuItem key={r.value} value={r.value}>{t(r.key)}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth label={t('cc_note_for_customer')} value={cancelNote}
            onChange={e => setCancelNote(e.target.value)} multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)} style={{ fontFamily: FONT_FAMILY }}>{t('cc_back')}</Button>
          <Button onClick={handleCancelSubmit} disabled={!cancelReason || submitting} color="error" variant="contained" style={{ fontFamily: FONT_FAMILY }}>
            {submitting ? <CircularProgress size={18} /> : t('cc_reject')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Acknowledge cancellation dialog */}
      <Dialog open={ackDialog} onClose={() => setAckDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontFamily: FONT_FAMILY }}>{t('cc_ack_title')}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label={t('cc_note_optional')} value={ackNote}
            onChange={e => setAckNote(e.target.value)} multiline rows={2} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAckDialog(false)} style={{ fontFamily: FONT_FAMILY }}>{t('cc_back')}</Button>
          <Button onClick={handleAckCancellation} disabled={submitting} variant="contained" style={{ fontFamily: FONT_FAMILY }}>
            {submitting ? <CircularProgress size={18} /> : t('cc_acknowledge')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast(t => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} style={{ fontFamily: FONT_FAMILY }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
